import { useId } from "react";
import { cn } from "@/lib/cn";

/* The flag for a language, drawn as an SVG so it looks the same on every
   platform — emoji flags render as two letters on Windows. Each is the
   country most readers associate with the language, cropped to a circle the
   way the kit draws avatars. */

const FLAGS: Record<string, (id: string) => React.ReactNode> = {
  en: (id) => (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
      <clipPath id={id}>
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath={`url(#${id})`} stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  ),
  sv: () => (
    <svg viewBox="0 0 16 10" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
      <rect width="16" height="10" fill="#006AA7" />
      <rect x="5" width="2" height="10" fill="#FECC02" />
      <rect y="4" width="16" height="2" fill="#FECC02" />
    </svg>
  ),
  de: () => (
    <svg viewBox="0 0 5 3" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
      <rect width="5" height="1" fill="#000" />
      <rect y="1" width="5" height="1" fill="#DD0000" />
      <rect y="2" width="5" height="1" fill="#FFCE00" />
    </svg>
  ),
  es: () => (
    <svg viewBox="0 0 3 2" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
      <rect width="3" height="2" fill="#AA151B" />
      <rect y="0.5" width="3" height="1" fill="#F1BF00" />
    </svg>
  ),
  pt: () => (
    <svg viewBox="0 0 30 20" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
      <rect width="30" height="20" fill="#FF0000" />
      <rect width="12" height="20" fill="#006600" />
      <circle cx="12" cy="10" r="4.5" fill="#FFE800" />
      <circle cx="12" cy="10" r="3" fill="#FF0000" />
      <rect x="10.4" y="8.4" width="3.2" height="3.2" fill="#fff" />
    </svg>
  ),
  "en-US": (id) => (
    <svg viewBox="0 0 19 10" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
      <clipPath id={id}>
        <rect width="19" height="10" />
      </clipPath>
      <g clipPath={`url(#${id})`}>
        <rect width="19" height="10" fill="#fff" />
        {[0, 2, 4, 6, 8].map((y) => (
          <rect key={y} y={y * 0.77} width="19" height="0.77" fill="#B22234" />
        ))}
        <rect width="7.6" height="4.6" fill="#3C3B6E" />
      </g>
    </svg>
  ),
  "es-MX": () => (
    <svg viewBox="0 0 3 2" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
      <rect width="1" height="2" fill="#006847" />
      <rect x="1" width="1" height="2" fill="#fff" />
      <rect x="2" width="1" height="2" fill="#CE1126" />
      <circle cx="1.5" cy="1" r="0.28" fill="#8A6D3B" />
    </svg>
  ),
  "pt-BR": () => (
    <svg viewBox="0 0 20 14" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
      <rect width="20" height="14" fill="#009C3B" />
      <path d="M10,1.5 L18.5,7 L10,12.5 L1.5,7 z" fill="#FFDF00" />
      <circle cx="10" cy="7" r="3.4" fill="#002776" />
    </svg>
  ),
};

export function Flag({ code, size = 20, className }: { code: string; size?: number; className?: string }) {
  const id = useId();
  const draw = FLAGS[code];

  /* An operator-added language has no drawn flag; its badge is the country
     (or language) letters, which reads unambiguously for any combination. */
  const letters = (code.split("-")[1] ?? code.split("-")[0] ?? "").slice(0, 2).toUpperCase();

  return (
    <span
      aria-hidden
      className={cn(
        "grid shrink-0 place-items-center overflow-hidden rounded-full bg-elevation-4 ring-1 ring-black/20",
        className,
      )}
      style={{ width: size, height: size }}
    >
      {draw ? (
        draw(id)
      ) : (
        <span className="font-bold text-fg-muted" style={{ fontSize: Math.round(size * 0.38) }}>
          {letters}
        </span>
      )}
    </span>
  );
}
